import { AD_PLACEMENTS, SUPPORTED_LANGUAGES, TOPIC_CATEGORIES } from '@topictalk/contracts';
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Eye,
  FileImage,
  Globe2,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Megaphone,
  Menu,
  MessageSquareWarning,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Upload,
  WalletCards,
  X,
} from 'lucide-react';
import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { PortalApiError, portalApi } from './api';
import { formatCurrency, formatDate, formatDateTime, formatNumber, initials, percentage, toLocalDateTimeInput } from './format';
import type {
  Campaign,
  CampaignAction,
  CampaignReport,
  CampaignState,
  MarketPolicyInput,
  MarketPolicyResult,
  ModerationReport,
  PortalMe,
  PortalRole,
  PortalView,
  ReviewCampaign,
} from './types';

const ROLE_LABELS: Record<PortalRole, string> = {
  advertiser_admin: 'Advertiser Admin',
  campaign_manager: 'Campaign Manager',
  platform_admin: 'Platform Admin',
  reviewer: 'Reviewer',
};

const STATE_LABELS: Record<CampaignState, string> = {
  draft: 'Entwurf',
  submitted: 'In Prüfung',
  approved: 'Freigegeben',
  active: 'Aktiv',
  paused: 'Pausiert',
  completed: 'Abgeschlossen',
  rejected: 'Abgelehnt',
};

const ACTION_LABELS: Record<CampaignAction, string> = {
  submit: 'Zur Prüfung',
  approve: 'Freigeben',
  reject: 'Ablehnen',
  activate: 'Aktivieren',
  pause: 'Pausieren',
  complete: 'Abschließen',
};

const NAV_ITEMS: Array<{ id: PortalView; label: string; icon: typeof LayoutDashboard; restricted?: 'admin' | 'review' }> = [
  { id: 'overview', label: 'Übersicht', icon: LayoutDashboard },
  { id: 'campaigns', label: 'Kampagnen', icon: Megaphone },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'market', label: 'Märkte', icon: Globe2, restricted: 'admin' },
  { id: 'moderation', label: 'Moderation', icon: ShieldCheck, restricted: 'review' },
];

function isReviewer(role: PortalRole): boolean {
  return role === 'platform_admin' || role === 'reviewer';
}

function toMessage(error: unknown): { title: string; detail?: string } {
  if (error instanceof PortalApiError) {
    return {
      title: error.message,
      detail: [error.code, error.requestId ? `Request ${error.requestId}` : undefined].filter(Boolean).join(' · '),
    };
  }
  return { title: error instanceof Error ? error.message : 'Ein unerwarteter Fehler ist aufgetreten.' };
}

export function App() {
  const [authState, setAuthState] = useState<'checking' | 'anonymous' | 'authenticated'>('checking');
  const [me, setMe] = useState<PortalMe | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [view, setView] = useState<PortalView>('overview');
  const [analyticsCampaignId, setAnalyticsCampaignId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; title: string; detail?: string } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleAuthExpired = useCallback((error: unknown) => {
    if (error instanceof PortalApiError && error.status === 401) {
      portalApi.clearToken();
      setMe(null);
      setCampaigns([]);
      setAuthState('anonymous');
      setNotice({ kind: 'error', title: 'Deine Sitzung ist abgelaufen.', detail: 'Bitte melde dich erneut an.' });
      return true;
    }
    return false;
  }, []);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const items = await portalApi.campaigns();
      setCampaigns(items);
      setAnalyticsCampaignId((current) => current ?? items[0]?.id ?? null);
    } catch (error) {
      if (!handleAuthExpired(error)) setNotice({ kind: 'error', ...toMessage(error) });
    } finally {
      setCampaignsLoading(false);
    }
  }, [handleAuthExpired]);

  useEffect(() => {
    let live = true;
    async function hydrate() {
      if (!portalApi.hasPersistedToken()) {
        setAuthState('anonymous');
        return;
      }
      try {
        const profile = await portalApi.me();
        if (!live) return;
        setMe(profile);
        setAuthState('authenticated');
      } catch (error) {
        if (!live) return;
        portalApi.clearToken();
        setAuthState('anonymous');
        if (!(error instanceof PortalApiError && error.status === 401)) {
          setNotice({ kind: 'error', ...toMessage(error) });
        }
      }
    }
    void hydrate();
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (authState === 'authenticated') void loadCampaigns();
  }, [authState, loadCampaigns]);

  async function onAuthenticated() {
    const profile = await portalApi.me();
    setMe(profile);
    setAuthState('authenticated');
    setNotice({ kind: 'success', title: `Willkommen bei ${profile.organization.display_name}.` });
  }

  function logout() {
    void portalApi.logout().catch(() => {
      // Local sign-out still succeeds if the API is temporarily unavailable.
    });
    setMe(null);
    setCampaigns([]);
    setView('overview');
    setAuthState('anonymous');
  }

  async function transitionCampaign(campaign: Campaign, action: CampaignAction) {
    try {
      const result = await portalApi.transitionCampaign(campaign.id, action);
      setCampaigns((items) => items.map((item) => (item.id === campaign.id ? { ...item, state: result.state } : item)));
      setNotice({ kind: 'success', title: `${campaign.name}: ${STATE_LABELS[result.state]}.` });
    } catch (error) {
      if (!handleAuthExpired(error)) setNotice({ kind: 'error', ...toMessage(error) });
    }
  }

  function navigate(next: PortalView) {
    setView(next);
    setSidebarOpen(false);
  }

  if (authState === 'checking') return <LoadingScreen />;
  if (authState === 'anonymous' || !me) {
    return (
      <>
        <AuthScreen onAuthenticated={onAuthenticated} />
        <Toast notice={notice} onClose={() => setNotice(null)} />
      </>
    );
  }

  const canAdmin = me.principal.role === 'platform_admin';
  const canReview = isReviewer(me.principal.role);

  return (
    <div className="app-shell">
      <Sidebar
        me={me}
        view={view}
        open={sidebarOpen}
        onNavigate={navigate}
        onLogout={logout}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="main-stage">
        <Topbar
          me={me}
          view={view}
          onOpenMenu={() => setSidebarOpen(true)}
          onCreate={() => navigate('create')}
        />
        <div className="content-stage">
          {view === 'overview' && (
            <Overview
              me={me}
              campaigns={campaigns}
              loading={campaignsLoading}
              onCreate={() => navigate('create')}
              onOpenCampaigns={() => navigate('campaigns')}
              onAnalytics={(id) => {
                setAnalyticsCampaignId(id);
                navigate('analytics');
              }}
            />
          )}
          {view === 'campaigns' && (
            <CampaignList
              campaigns={campaigns}
              loading={campaignsLoading}
              role={me.principal.role}
              onRefresh={loadCampaigns}
              onCreate={() => navigate('create')}
              onTransition={transitionCampaign}
              onAnalytics={(id) => {
                setAnalyticsCampaignId(id);
                navigate('analytics');
              }}
              onSuccess={(message) => setNotice({ kind: 'success', title: message })}
              onError={(error) => {
                if (!handleAuthExpired(error)) setNotice({ kind: 'error', ...toMessage(error) });
              }}
            />
          )}
          {view === 'create' && (
            <CampaignCreate
              onCancel={() => navigate('campaigns')}
              onCreated={async (name) => {
                await loadCampaigns();
                setNotice({ kind: 'success', title: `${name} wurde als Entwurf angelegt.` });
                navigate('campaigns');
              }}
              onError={(error) => {
                if (!handleAuthExpired(error)) setNotice({ kind: 'error', ...toMessage(error) });
              }}
            />
          )}
          {view === 'analytics' && (
            <Analytics
              campaigns={campaigns}
              selectedId={analyticsCampaignId}
              onSelectedId={setAnalyticsCampaignId}
              onError={(error) => {
                if (!handleAuthExpired(error)) setNotice({ kind: 'error', ...toMessage(error) });
              }}
              onCreate={() => navigate('create')}
            />
          )}
          {view === 'market' && canAdmin && (
              <MarketPolicies
                onSuccess={(message) => setNotice({ kind: 'success', title: message })}
                onError={(error) => {
                  if (!handleAuthExpired(error)) setNotice({ kind: 'error', ...toMessage(error) });
                }}
              />
          )}
          {view === 'moderation' && canReview && (
            <Moderation
              role={me.principal.role}
              onSuccess={(message) => setNotice({ kind: 'success', title: message })}
              onError={(error) => {
                if (!handleAuthExpired(error)) setNotice({ kind: 'error', ...toMessage(error) });
              }}
            />
          )}
          {view === 'market' && !canAdmin && <RestrictedState />}
          {view === 'moderation' && !canReview && <RestrictedState />}
        </div>
      </main>
      <Toast notice={notice} onClose={() => setNotice(null)} />
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <Logo compact />
      <LoaderCircle className="spin" aria-hidden="true" />
      <span>Campaign Studio wird geladen</span>
    </div>
  );
}

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? 'brand--compact' : ''}`} aria-label="TopicTalk Campaign Studio">
      <span className="brand-mark" aria-hidden="true">
        <span />
        <span />
      </span>
      {!compact && (
        <span className="brand-copy">
          <strong>TopicTalk</strong>
          <small>Campaign Studio</small>
        </span>
      )}
    </div>
  );
}

function AuthScreen({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [mode, setMode] = useState<'login' | 'bootstrap'>('login');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ title: string; detail?: string } | null>(null);

  async function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const values = new FormData(event.currentTarget);
    try {
      await portalApi.login(String(values.get('email')), String(values.get('password')));
      await onAuthenticated();
    } catch (caught) {
      portalApi.clearToken();
      setError(toMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitBootstrap(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const values = new FormData(event.currentTarget);
    try {
      await portalApi.bootstrap({
        email: String(values.get('email')),
        password: String(values.get('password')),
        legalName: String(values.get('legalName')),
        displayName: String(values.get('displayName')),
        countryCode: String(values.get('countryCode')).toUpperCase(),
        bootstrapKey: String(values.get('bootstrapKey')),
      });
      await onAuthenticated();
    } catch (caught) {
      portalApi.clearToken();
      setError(toMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <section className="auth-story">
        <Logo />
        <div className="auth-story__content">
          <p className="eyebrow"><Radio size={14} /> Werbung im richtigen Moment</p>
          <h1>Kontext wird zur <span>stärksten Zielgruppe.</span></h1>
          <p>
            Plane Kampagnen für echte Gesprächsmomente – transparent, thematisch passend und ohne dauerhaftes Personenprofil.
          </p>
          <div className="auth-proof">
            <div><Target aria-hidden="true" /><span><strong>Sessiongenau</strong> statt personenbasiert</span></div>
            <div><Eye aria-hidden="true" /><span><strong>Messbar</strong> ab sicherer Kohortengröße</span></div>
            <div><ShieldCheck aria-hidden="true" /><span><strong>Kontrolliert</strong> durch Markt- und Themenregeln</span></div>
          </div>
        </div>
        <p className="auth-story__foot">TopicTalk · Erst das Thema. Dann das Gespräch.</p>
      </section>
      <section className="auth-panel">
        <div className="auth-panel__inner">
          <div className="auth-mobile-brand"><Logo /></div>
          <p className="eyebrow">Geschützter Bereich</p>
          <h2>{mode === 'login' ? 'Willkommen zurück' : 'Portal initialisieren'}</h2>
          <p className="muted">
            {mode === 'login'
              ? 'Melde dich mit deinem Werbekunden-Konto an.'
              : 'Einmalige Einrichtung des ersten Plattform-Administrators.'}
          </p>
          <div className="auth-tabs" role="tablist" aria-label="Zugang auswählen">
            <button className={mode === 'login' ? 'active' : ''} onClick={() => { setMode('login'); setError(null); }} type="button">Anmelden</button>
            <button className={mode === 'bootstrap' ? 'active' : ''} onClick={() => { setMode('bootstrap'); setError(null); }} type="button">Ersteinrichtung</button>
          </div>
          {error && <InlineError title={error.title} detail={error.detail} />}
          {mode === 'login' ? (
            <form className="auth-form" onSubmit={submitLogin}>
              <Field label="E-Mail-Adresse" htmlFor="login-email">
                <input id="login-email" name="email" type="email" autoComplete="username" required placeholder="name@unternehmen.at" />
              </Field>
              <Field label="Passwort" htmlFor="login-password">
                <input id="login-password" name="password" type="password" minLength={12} autoComplete="current-password" required placeholder="Mindestens 12 Zeichen" />
              </Field>
              <button className="button button--primary button--wide" disabled={submitting} type="submit">
                {submitting ? <LoaderCircle className="spin" /> : <LockKeyhole />}
                {submitting ? 'Verbindung wird geprüft' : 'Sicher anmelden'}
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={submitBootstrap}>
              <div className="field-grid">
                <Field label="Firmenname" htmlFor="bootstrap-legal"><input id="bootstrap-legal" name="legalName" required minLength={2} maxLength={120} /></Field>
                <Field label="Anzeigename" htmlFor="bootstrap-display"><input id="bootstrap-display" name="displayName" required minLength={2} maxLength={80} /></Field>
              </div>
              <div className="field-grid field-grid--country">
                <Field label="Land" htmlFor="bootstrap-country"><input id="bootstrap-country" name="countryCode" defaultValue="AT" pattern="[A-Za-z]{2}" maxLength={2} required /></Field>
                <Field label="E-Mail-Adresse" htmlFor="bootstrap-email"><input id="bootstrap-email" name="email" type="email" autoComplete="username" required /></Field>
              </div>
              <Field label="Passwort" htmlFor="bootstrap-password"><input id="bootstrap-password" name="password" type="password" minLength={12} maxLength={200} autoComplete="new-password" required /></Field>
              <Field label="Bootstrap-Schlüssel" htmlFor="bootstrap-key" hint="Wird nur für diese Anfrage verwendet und nicht im Browser gespeichert.">
                <input id="bootstrap-key" name="bootstrapKey" type="password" autoComplete="off" required />
              </Field>
              <button className="button button--primary button--wide" disabled={submitting} type="submit">
                {submitting ? <LoaderCircle className="spin" /> : <Sparkles />}
                {submitting ? 'Portal wird eingerichtet' : 'Portal einrichten'}
              </button>
            </form>
          )}
          <div className="security-note"><ShieldCheck /><span>Zugangsdaten werden verschlüsselt übertragen. Das Portal speichert nur das von der API ausgegebene Sitzungstoken.</span></div>
        </div>
      </section>
    </div>
  );
}

function Sidebar({
  me,
  view,
  open,
  onNavigate,
  onLogout,
  onClose,
}: {
  me: PortalMe;
  view: PortalView;
  open: boolean;
  onNavigate: (view: PortalView) => void;
  onLogout: () => void;
  onClose: () => void;
}) {
  const items = NAV_ITEMS.filter((item) => {
    if (item.restricted === 'admin') return me.principal.role === 'platform_admin';
    if (item.restricted === 'review') return isReviewer(me.principal.role);
    return true;
  });
  return (
    <>
      <div className={`sidebar-backdrop ${open ? 'show' : ''}`} onClick={onClose} aria-hidden="true" />
      <aside className={`sidebar ${open ? 'sidebar--open' : ''}`}>
        <div className="sidebar__top">
          <Logo />
          <button className="icon-button sidebar-close" onClick={onClose} aria-label="Menü schließen"><X /></button>
        </div>
        <nav className="nav-list" aria-label="Hauptnavigation">
          <p className="nav-label">Workspace</p>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} className={view === item.id ? 'active' : ''} onClick={() => onNavigate(item.id)} type="button">
                <Icon aria-hidden="true" />
                <span>{item.label}</span>
                {view === item.id && <span className="nav-active-dot" />}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-card">
          <span className={`status-dot status-dot--${me.organization.verification_status}`} />
          <div>
            <small>Organisation</small>
            <strong>{me.organization.display_name}</strong>
            <span>{me.organization.verification_status === 'verified' ? 'Verifiziert' : me.organization.verification_status === 'pending' ? 'Prüfung läuft' : 'Nicht freigegeben'}</span>
          </div>
        </div>
        <div className="account-card">
          <span className="avatar">{initials(me.organization.display_name)}</span>
          <div><strong>{me.principal.email}</strong><span>{ROLE_LABELS[me.principal.role]}</span></div>
          <button className="icon-button" onClick={onLogout} aria-label="Abmelden" title="Abmelden"><LogOut /></button>
        </div>
      </aside>
    </>
  );
}

function Topbar({ me, view, onOpenMenu, onCreate }: { me: PortalMe; view: PortalView; onOpenMenu: () => void; onCreate: () => void }) {
  const title = view === 'create' ? 'Neue Kampagne' : NAV_ITEMS.find((item) => item.id === view)?.label ?? 'Campaign Studio';
  return (
    <header className="topbar">
      <div className="topbar__title">
        <button className="icon-button mobile-menu" onClick={onOpenMenu} aria-label="Navigation öffnen"><Menu /></button>
        <div><span>{me.organization.display_name}</span><h1>{title}</h1></div>
      </div>
      <div className="topbar__actions">
        <span className="connection-chip"><span /> API verbunden</span>
        {view !== 'create' && <button className="button button--primary" onClick={onCreate}><Plus /> Kampagne</button>}
      </div>
    </header>
  );
}

function Overview({
  me,
  campaigns,
  loading,
  onCreate,
  onOpenCampaigns,
  onAnalytics,
}: {
  me: PortalMe;
  campaigns: Campaign[];
  loading: boolean;
  onCreate: () => void;
  onOpenCampaigns: () => void;
  onAnalytics: (id: string) => void;
}) {
  const active = campaigns.filter((campaign) => campaign.state === 'active').length;
  const budget = campaigns.reduce((sum, campaign) => sum + campaign.budget_cents, 0);
  const spent = campaigns.reduce((sum, campaign) => sum + campaign.spent_cents, 0);
  const latest = campaigns.slice(0, 5);
  return (
    <div className="page-stack">
      <section className="welcome-row">
        <div>
          <p className="eyebrow">Live Campaign Workspace</p>
          <h2>Guten Tag, {me.organization.display_name}.</h2>
          <p className="muted">Steuere relevante Werbemomente entlang echter TopicTalk-Sessions.</p>
        </div>
        <div className="date-chip"><CalendarDays /><span><small>Heute</small>{new Intl.DateTimeFormat('de-AT', { weekday: 'short', day: '2-digit', month: 'long' }).format(new Date())}</span></div>
      </section>
      <section className="kpi-grid" aria-label="Kampagnenkennzahlen">
        <KpiCard icon={Megaphone} label="Kampagnen" value={formatNumber(campaigns.length)} meta={campaigns.length ? 'im Workspace' : 'noch keine angelegt'} />
        <KpiCard icon={Radio} label="Aktiv" value={formatNumber(active)} meta={active ? 'werden aktuell ausgeliefert' : 'keine Live-Auslieferung'} accent />
        <KpiCard icon={WalletCards} label="Gesamtbudget" value={formatCurrency(budget)} meta="über alle Kampagnen" />
        <KpiCard icon={TrendingUp} label="Ausgegeben" value={formatCurrency(spent)} meta={budget ? `${percentage(spent, budget)} des Budgets` : 'noch keine Ausgaben'} />
      </section>
      <section className="overview-grid">
        <div className="panel panel--campaigns">
          <PanelHeader eyebrow="Campaign Control" title="Aktuelle Kampagnen" action={<button className="text-button" onClick={onOpenCampaigns}>Alle anzeigen <ArrowRight /></button>} />
          {loading ? <LoadingRows /> : latest.length ? (
            <CampaignTable campaigns={latest} compact onAnalytics={onAnalytics} />
          ) : (
            <EmptyState icon={Megaphone} title="Bereit für die erste Kampagne" text="Lege Targeting, Gesprächsphase und Budget fest. Die API speichert sie zunächst als Entwurf." action={<button className="button button--primary" onClick={onCreate}><Plus /> Kampagne anlegen</button>} />
          )}
        </div>
        <div className="panel attention-panel">
          <div className="attention-panel__glow" />
          <p className="eyebrow">TopicTalk Attention Lane</p>
          <h3>Drei klar markierte Kontaktpunkte.</h3>
          <p className="muted">Ein Motiv kann vor, während oder nach einer Session erscheinen. Jede Ausspielung bleibt an den gemeinsamen Kontext gebunden.</p>
          <ol className="lane-list">
            <li><span>01</span><div><strong>PRE</strong><small>Vor dem Gespräch</small></div></li>
            <li><span>02</span><div><strong>LIVE</strong><small>Im strukturierten Übergang</small></div></li>
            <li><span>03</span><div><strong>POST</strong><small>Nach der Session</small></div></li>
          </ol>
          <div className="privacy-chip"><ShieldCheck /> Ohne sessionsübergreifendes Personenprofil</div>
        </div>
      </section>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, meta, accent = false }: { icon: typeof Megaphone; label: string; value: string; meta: string; accent?: boolean }) {
  return (
    <article className={`kpi-card ${accent ? 'kpi-card--accent' : ''}`}>
      <span className="kpi-icon"><Icon /></span>
      <div><span>{label}</span><strong>{value}</strong><small>{meta}</small></div>
    </article>
  );
}

function PanelHeader({ eyebrow, title, action }: { eyebrow: string; title: string; action?: ReactNode }) {
  return <div className="panel-header"><div><p className="eyebrow">{eyebrow}</p><h3>{title}</h3></div>{action}</div>;
}

function CampaignList({
  campaigns,
  loading,
  role,
  onRefresh,
  onCreate,
  onTransition,
  onAnalytics,
  onSuccess,
  onError,
}: {
  campaigns: Campaign[];
  loading: boolean;
  role: PortalRole;
  onRefresh: () => Promise<void>;
  onCreate: () => void;
  onTransition: (campaign: Campaign, action: CampaignAction) => Promise<void>;
  onAnalytics: (id: string) => void;
  onSuccess: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<'all' | CampaignState>('all');
  const visible = campaigns.filter((campaign) => {
    const matchesState = state === 'all' || campaign.state === state;
    const needle = query.trim().toLowerCase();
    return matchesState && (!needle || `${campaign.name} ${campaign.sponsor_name}`.toLowerCase().includes(needle));
  });
  return (
    <div className="page-stack">
      <PageIntro eyebrow="Campaign Control" title="Kampagnen" text="Von der Idee bis zur aktiven Session-Auslieferung – jeder Statuswechsel wird von der API geprüft." />
      {isReviewer(role) && <CampaignReviewQueue onSuccess={onSuccess} onError={onError} />}
      <section className="panel">
        <div className="toolbar">
          <div className="search-field"><Search /><input aria-label="Kampagnen suchen" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Kampagne oder Sponsor suchen" /></div>
          <div className="toolbar__right">
            <label className="select-field"><span className="sr-only">Status filtern</span><select value={state} onChange={(event) => setState(event.target.value as 'all' | CampaignState)}><option value="all">Alle Status</option>{Object.entries(STATE_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><ChevronDown /></label>
            <button className="button button--secondary button--icon" onClick={() => void onRefresh()} disabled={loading} aria-label="Kampagnen neu laden"><RefreshCw className={loading ? 'spin' : ''} /></button>
            <button className="button button--primary" onClick={onCreate}><Plus /> Neu</button>
          </div>
        </div>
        {loading ? <LoadingRows /> : visible.length ? (
          <CampaignTable campaigns={visible} role={role} onTransition={onTransition} onAnalytics={onAnalytics} />
        ) : (
          <EmptyState icon={Search} title={campaigns.length ? 'Keine Treffer' : 'Noch keine Kampagnen'} text={campaigns.length ? 'Passe Suche oder Statusfilter an.' : 'Erstelle die erste Kampagne direkt gegen die TopicTalk-API.'} action={!campaigns.length ? <button className="button button--primary" onClick={onCreate}><Plus /> Erste Kampagne</button> : undefined} />
        )}
      </section>
    </div>
  );
}

function CampaignReviewQueue({ onSuccess, onError }: { onSuccess: (message: string) => void; onError: (error: unknown) => void }) {
  const [items, setItems] = useState<ReviewCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await portalApi.reviewCampaigns());
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function review(item: ReviewCampaign, action: 'approve' | 'reject') {
    setBusyId(item.id);
    try {
      await portalApi.transitionCampaign(item.id, action);
      setItems((current) => current.filter((campaign) => campaign.id !== item.id));
      onSuccess(`${item.name} wurde ${action === 'approve' ? 'freigegeben' : 'abgelehnt'}.`);
    } catch (error) {
      onError(error);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="panel review-queue">
      <PanelHeader
        eyebrow="Plattformweite Prüfung"
        title="Eingereichte Kampagnen"
        action={<button className="button button--secondary button--icon" onClick={() => void load()} disabled={loading} aria-label="Prüfwarteschlange neu laden"><RefreshCw className={loading ? 'spin' : ''} /></button>}
      />
      <p className="review-queue__notice"><ShieldCheck /> Die Queue zeigt nur prüfungsrelevante Kampagnendaten; Mandanten-Zugangsdaten und rechtliche Stammdaten bleiben verborgen.</p>
      {loading ? <LoadingRows /> : items.length ? (
        <div className="review-cards">
          {items.map((item) => (
            <article className="review-card" key={item.id}>
              <div className="review-card__creative">{item.creativeAssetUrl ? <img src={item.creativeAssetUrl} alt="" /> : <Megaphone />}</div>
              <div className="review-card__content">
                <div className="review-card__meta"><span>{item.organization.displayName} · {item.organization.countryCode}</span><span>{formatCurrency(item.budgetCents)}</span></div>
                <h4>{item.headline}</h4>
                <p>{item.body}</p>
                <small>{item.sponsorName} · {item.ctaLabel} · {new URL(item.destinationUrl).hostname}</small>
                <div className="review-tags">
                  {[...item.targeting.countryCodes, ...item.targeting.languages.map((value) => value.toUpperCase()), ...item.targeting.categoryIds, ...item.targeting.placements.map((value) => value.toUpperCase())].map((value, index) => <span key={`${value}-${index}`}>{value}</span>)}
                </div>
              </div>
              <div className="review-card__actions">
                <button className="button button--secondary button--small" disabled={busyId === item.id} onClick={() => void review(item, 'reject')}><X /> Ablehnen</button>
                <button className="button button--primary button--small" disabled={busyId === item.id} onClick={() => void review(item, 'approve')}>{busyId === item.id ? <LoaderCircle className="spin" /> : <Check />} Freigeben</button>
              </div>
            </article>
          ))}
        </div>
      ) : <EmptyState icon={CheckCircle2} title="Prüfqueue ist leer" text="Derzeit wartet keine eingereichte Kampagne auf eine Entscheidung." />}
    </section>
  );
}

function CampaignTable({
  campaigns,
  compact = false,
  role,
  onTransition,
  onAnalytics,
}: {
  campaigns: Campaign[];
  compact?: boolean;
  role?: PortalRole;
  onTransition?: (campaign: Campaign, action: CampaignAction) => Promise<void>;
  onAnalytics: (id: string) => void;
}) {
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead><tr><th>Kampagne</th><th>Status</th><th>Laufzeit</th><th>Budget</th>{!compact && <th>Auslastung</th>}<th><span className="sr-only">Aktionen</span></th></tr></thead>
        <tbody>
          {campaigns.map((campaign) => (
            <tr key={campaign.id}>
              <td><div className="campaign-cell"><span className="campaign-thumb">{campaign.creative_asset_url ? <img src={campaign.creative_asset_url} alt="" /> : <Megaphone />}</span><div><strong>{campaign.name}</strong><span>{campaign.sponsor_name}</span></div></div></td>
              <td><StatusPill state={campaign.state} /></td>
              <td><div className="date-cell"><strong>{formatDate(campaign.starts_at)}</strong><span>bis {formatDate(campaign.ends_at)}</span></div></td>
              <td><div className="money-cell"><strong>{formatCurrency(campaign.budget_cents)}</strong><span>{formatCurrency(campaign.spent_cents)} genutzt</span></div></td>
              {!compact && <td><BudgetBar spent={campaign.spent_cents} budget={campaign.budget_cents} /> </td>}
              <td><div className="row-actions"><button className="icon-button" onClick={() => onAnalytics(campaign.id)} aria-label={`Analytics für ${campaign.name}`} title="Analytics"><BarChart3 /></button>{role && onTransition && <CampaignActions campaign={campaign} role={role} onTransition={onTransition} />}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function actionsFor(campaign: Campaign, role: PortalRole): CampaignAction[] {
  if (campaign.state === 'draft') return ['submit'];
  if (campaign.state === 'submitted') return isReviewer(role) ? ['approve', 'reject'] : [];
  if (campaign.state === 'approved') return ['activate'];
  if (campaign.state === 'active') return ['pause', 'complete'];
  if (campaign.state === 'paused') return ['activate', 'complete'];
  return [];
}

function CampaignActions({ campaign, role, onTransition }: { campaign: Campaign; role: PortalRole; onTransition: (campaign: Campaign, action: CampaignAction) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<CampaignAction | null>(null);
  const actions = actionsFor(campaign, role);
  if (!actions.length) return null;
  async function act(action: CampaignAction) {
    setBusy(action);
    try {
      await onTransition(campaign, action);
      setOpen(false);
    } finally {
      setBusy(null);
    }
  }
  return (
    <div className="action-menu">
      <button className="icon-button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label={`Aktionen für ${campaign.name}`}><MoreHorizontal /></button>
      {open && <div className="action-menu__popover">{actions.map((action) => <button key={action} disabled={Boolean(busy)} onClick={() => void act(action)}>{busy === action ? <LoaderCircle className="spin" /> : action === 'activate' ? <Play /> : action === 'pause' ? <Pause /> : action === 'submit' ? <Send /> : action === 'approve' ? <Check /> : action === 'reject' ? <X /> : <CheckCircle2 />}{ACTION_LABELS[action]}</button>)}</div>}
    </div>
  );
}

function StatusPill({ state }: { state: CampaignState }) {
  return <span className={`status-pill status-pill--${state}`}><span />{STATE_LABELS[state]}</span>;
}

function BudgetBar({ spent, budget }: { spent: number; budget: number }) {
  const progress = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  return <div className="budget-bar"><div><span style={{ width: `${progress}%` }} /></div><small>{Math.round(progress)} %</small></div>;
}

function CampaignCreate({ onCancel, onCreated, onError }: { onCancel: () => void; onCreated: (name: string) => Promise<void>; onError: (error: unknown) => void }) {
  const start = useMemo(() => new Date(Date.now() + 24 * 60 * 60 * 1000), []);
  const end = useMemo(() => new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000), [start]);
  const [languages, setLanguages] = useState<string[]>(['de']);
  const [categories, setCategories] = useState<string[]>(['career']);
  const [placements, setPlacements] = useState<string[]>(['pre', 'post']);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function toggle(value: string, values: string[], setter: (values: string[]) => void) {
    setter(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    const data = new FormData(event.currentTarget);
    const countries = String(data.get('countries')).split(',').map((value) => value.trim().toUpperCase()).filter(Boolean);
    const tags = String(data.get('contextTags')).split(',').map((value) => value.trim().toLowerCase()).filter(Boolean);
    const startsAt = new Date(String(data.get('startsAt')));
    const endsAt = new Date(String(data.get('endsAt')));
    const budgetEuros = Number(data.get('budgetEuros'));
    if (!languages.length || !categories.length || !placements.length || !countries.length) {
      setFormError('Wähle mindestens ein Land, eine Sprache, ein Thema und eine Platzierung.');
      return;
    }
    if (startsAt >= endsAt) {
      setFormError('Das Kampagnenende muss nach dem Start liegen.');
      return;
    }
    if (file && (file.size > 5 * 1024 * 1024 || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type))) {
      setFormError('Das Creative muss PNG, JPEG oder WebP und maximal 5 MB groß sein.');
      return;
    }
    setSubmitting(true);
    try {
      const creative = file ? await portalApi.uploadCreative(file) : null;
      const name = String(data.get('name'));
      await portalApi.createCampaign({
        name,
        sponsorName: String(data.get('sponsorName')),
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        budgetCents: Math.round(budgetEuros * 100),
        countryCodes: countries,
        languages: languages as Parameters<typeof portalApi.createCampaign>[0]['languages'],
        categoryIds: categories as Parameters<typeof portalApi.createCampaign>[0]['categoryIds'],
        contextTags: tags,
        placements: placements as Parameters<typeof portalApi.createCampaign>[0]['placements'],
        headline: String(data.get('headline')),
        body: String(data.get('body')),
        ctaLabel: String(data.get('ctaLabel')),
        destinationUrl: String(data.get('destinationUrl')),
        ...(creative ? { creativeAssetUrl: creative.assetUrl } : {}),
      });
      await onCreated(name);
    } catch (error) {
      onError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-stack page-stack--narrow">
      <PageIntro eyebrow="Campaign Builder" title="Neue Kampagne" text="Definiere Kontext, Gesprächsphase und Motiv. Die Kampagne bleibt bis zur Einreichung ein Entwurf." />
      <form className="campaign-form" onSubmit={submit}>
        {formError && <InlineError title={formError} />}
        <section className="form-section">
          <FormSectionHeader step="01" title="Grundlage" text="Interner Name, Sponsor und Kampagnenzeitraum." />
          <div className="form-section__fields">
            <div className="field-grid"><Field label="Kampagnenname" htmlFor="campaign-name"><input id="campaign-name" name="name" minLength={2} maxLength={100} required placeholder="Herbstkampagne Bahnreisen" /></Field><Field label="Sichtbarer Sponsor" htmlFor="campaign-sponsor"><input id="campaign-sponsor" name="sponsorName" minLength={2} maxLength={100} required placeholder="Beispiel GmbH" /></Field></div>
            <div className="field-grid"><Field label="Start" htmlFor="campaign-start"><input id="campaign-start" name="startsAt" type="datetime-local" defaultValue={toLocalDateTimeInput(start)} required /></Field><Field label="Ende" htmlFor="campaign-end"><input id="campaign-end" name="endsAt" type="datetime-local" defaultValue={toLocalDateTimeInput(end)} required /></Field></div>
            <Field label="Gesamtbudget in EUR" htmlFor="campaign-budget" hint="Die API führt Beträge intern in Cent."><div className="input-prefix"><span>€</span><input id="campaign-budget" name="budgetEuros" type="number" min="1" step="0.01" required placeholder="2500" /></div></Field>
          </div>
        </section>
        <section className="form-section">
          <FormSectionHeader step="02" title="Kontext" text="Nur zulässige, explizite Sessionmerkmale werden für die Auswahl verwendet." />
          <div className="form-section__fields">
            <Field label="Zielländer" htmlFor="campaign-countries" hint="ISO-Ländercodes, mit Komma getrennt. Russland wird serverseitig abgewiesen."><input id="campaign-countries" name="countries" defaultValue="AT, DE, CH" required /></Field>
            <ChoiceGroup label="Sprachen" values={SUPPORTED_LANGUAGES.map((item) => ({ value: item.code, label: item.label }))} selected={languages} onToggle={(value) => toggle(value, languages, setLanguages)} />
            <ChoiceGroup label="Gesprächsthemen" wide values={TOPIC_CATEGORIES.map((item) => ({ value: item.id, label: item.labelDe, meta: item.adSensitivity }))} selected={categories} onToggle={(value) => toggle(value, categories, setCategories)} />
            <Field label="Live-Kontext-Tags" htmlFor="campaign-tags" hint="Optional, z. B. travel.rail, career.startup. Sensible Tags werden von der Auslieferung zusätzlich gefiltert."><input id="campaign-tags" name="contextTags" placeholder="travel.rail, travel.city" /></Field>
            <ChoiceGroup label="Platzierungen" values={AD_PLACEMENTS.map((value) => ({ value, label: value === 'pre' ? 'PRE · Vor der Session' : value === 'live' ? 'LIVE · Im Übergang' : 'POST · Nach der Session' }))} selected={placements} onToggle={(value) => toggle(value, placements, setPlacements)} />
          </div>
        </section>
        <section className="form-section">
          <FormSectionHeader step="03" title="Motiv" text="Klar gekennzeichnet, kompakt und für den Gesprächsmoment optimiert." />
          <div className="form-section__fields">
            <Field label="Headline" htmlFor="campaign-headline"><input id="campaign-headline" name="headline" minLength={2} maxLength={90} required placeholder="Deine nächste Reise beginnt im Gespräch." /></Field>
            <Field label="Anzeigentext" htmlFor="campaign-body"><textarea id="campaign-body" name="body" minLength={2} maxLength={220} rows={4} required placeholder="Kurzer, relevanter Text ohne irreführende Versprechen." /></Field>
            <div className="field-grid"><Field label="CTA-Text" htmlFor="campaign-cta"><input id="campaign-cta" name="ctaLabel" minLength={2} maxLength={35} required placeholder="Angebot entdecken" /></Field><Field label="Ziel-URL" htmlFor="campaign-url"><input id="campaign-url" name="destinationUrl" type="url" pattern="https://.*" required placeholder="https://unternehmen.at/aktion" /></Field></div>
            <Field label="Creative" htmlFor="campaign-file" hint="Optional · PNG, JPEG oder WebP · maximal 5 MB">
              <label className={`file-drop ${file ? 'file-drop--selected' : ''}`} htmlFor="campaign-file"><input id="campaign-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /><span className="file-drop__icon">{file ? <FileImage /> : <Upload />}</span><span><strong>{file?.name ?? 'Creative auswählen'}</strong><small>{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB · wird beim Speichern hochgeladen` : 'Datei hier auswählen'}</small></span>{file && <CheckCircle2 className="file-check" />}</label>
            </Field>
          </div>
        </section>
        <div className="form-actions"><button className="button button--ghost" type="button" onClick={onCancel}>Abbrechen</button><button className="button button--primary" disabled={submitting} type="submit">{submitting ? <LoaderCircle className="spin" /> : <Check />} {submitting ? 'Entwurf wird gespeichert' : 'Als Entwurf speichern'}</button></div>
      </form>
    </div>
  );
}

function FormSectionHeader({ step, title, text }: { step: string; title: string; text: string }) {
  return <div className="form-section__header"><span>{step}</span><div><h3>{title}</h3><p>{text}</p></div></div>;
}

function ChoiceGroup({ label, values, selected, onToggle, wide = false }: { label: string; values: Array<{ value: string; label: string; meta?: string }>; selected: string[]; onToggle: (value: string) => void; wide?: boolean }) {
  return (
    <fieldset className="choice-field"><legend>{label}</legend><div className={`choice-grid ${wide ? 'choice-grid--wide' : ''}`}>{values.map((item) => { const active = selected.includes(item.value); return <button className={active ? 'active' : ''} aria-pressed={active} key={item.value} type="button" onClick={() => onToggle(item.value)}><span className="choice-check">{active && <Check />}</span><span>{item.label}{item.meta && <small>{item.meta}</small>}</span></button>; })}</div></fieldset>
  );
}

function Analytics({ campaigns, selectedId, onSelectedId, onError, onCreate }: { campaigns: Campaign[]; selectedId: string | null; onSelectedId: (id: string) => void; onError: (error: unknown) => void; onCreate: () => void }) {
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [loading, setLoading] = useState(false);
  const selected = campaigns.find((campaign) => campaign.id === selectedId) ?? campaigns[0];

  useEffect(() => {
    if (!selected) { setReport(null); return; }
    let live = true;
    setLoading(true);
    portalApi.campaignReport(selected.id).then((value) => { if (live) setReport(value); }).catch((error) => { if (live) onError(error); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [selected?.id]);

  if (!selected) return <div className="page-stack"><PageIntro eyebrow="Privacy-safe Reporting" title="Analytics" text="Aggregierte Kampagnenwerte erscheinen ab der konfigurierten Mindestkohorte." /><section className="panel"><EmptyState icon={BarChart3} title="Noch keine Kampagne" text="Analytics stehen bereit, sobald du eine Kampagne angelegt hast." action={<button className="button button--primary" onClick={onCreate}><Plus /> Kampagne anlegen</button>} /></section></div>;

  const rows = report?.rows ?? [];
  const impressions = rows.reduce((sum, row) => sum + row.impressions, 0);
  const interactions = rows.reduce((sum, row) => sum + row.interactions, 0);
  const relevanceRows = rows.filter((row) => row.relevance !== null);
  const relevance = relevanceRows.length ? relevanceRows.reduce((sum, row) => sum + (row.relevance ?? 0), 0) / relevanceRows.length : null;
  const maximum = Math.max(1, ...rows.map((row) => row.impressions));
  return (
    <div className="page-stack">
      <div className="analytics-head"><PageIntro eyebrow="Privacy-safe Reporting" title="Analytics" text="Nur aggregierte Kampagnenwerte oberhalb der serverseitigen Mindestkohorte." /><label className="campaign-picker"><span>Kampagne</span><select value={selected.id} onChange={(event) => onSelectedId(event.target.value)}>{campaigns.map((campaign) => <option value={campaign.id} key={campaign.id}>{campaign.name}</option>)}</select><ChevronDown /></label></div>
      <section className="kpi-grid kpi-grid--analytics">
        <KpiCard icon={Eye} label="Impressions" value={loading ? '…' : formatNumber(impressions)} meta="freigegebene Kohorten" />
        <KpiCard icon={Target} label="Interaktionen" value={loading ? '…' : formatNumber(interactions)} meta={percentage(interactions, impressions)} accent />
        <KpiCard icon={Sparkles} label="Relevanz" value={loading ? '…' : relevance === null ? '–' : relevance.toFixed(1)} meta="von 5 möglichen Punkten" />
        <KpiCard icon={CircleDollarSign} label="Spend" value={formatCurrency(selected.spent_cents)} meta={`${percentage(selected.spent_cents, selected.budget_cents)} des Budgets`} />
      </section>
      <section className="analytics-grid">
        <div className="panel chart-panel">
          <PanelHeader eyebrow="Auslieferung" title="Impressions nach Kohorte" />
          {loading ? <div className="chart-loading"><LoaderCircle className="spin" /></div> : rows.length ? <div className="bar-chart">{rows.slice(0, 12).reverse().map((row, index) => <div className="bar-row" key={`${row.day}-${row.country_cluster}-${row.language}-${index}`}><span>{new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit' }).format(new Date(`${row.day}T00:00:00Z`))}</span><div><i style={{ width: `${Math.max(3, (row.impressions / maximum) * 100)}%` }} /><b>{formatNumber(row.impressions)}</b></div></div>)}</div> : <EmptyState icon={BarChart3} title="Noch keine freigegebenen Messwerte" text={`Kohorten werden erst ab mindestens ${report?.minimumCohort ?? '–'} Impressions angezeigt.`} />}
        </div>
        <div className="panel insight-panel">
          <PanelHeader eyebrow="Kampagne" title={selected.name} />
          <dl className="detail-list"><div><dt>Status</dt><dd><StatusPill state={selected.state} /></dd></div><div><dt>Budget</dt><dd>{formatCurrency(selected.budget_cents)}</dd></div><div><dt>Laufzeit</dt><dd>{formatDate(selected.starts_at)} – {formatDate(selected.ends_at)}</dd></div><div><dt>Sponsor</dt><dd>{selected.sponsor_name}</dd></div><div><dt>Mindestkohorte</dt><dd>{report ? `${report.minimumCohort} Impressions` : '–'}</dd></div></dl>
          <div className="privacy-card"><ShieldCheck /><div><strong>Aggregiert by design</strong><span>Keine Nutzer-, Geräte- oder Sessionkennungen werden im Kampagnenreport ausgegeben.</span></div></div>
        </div>
      </section>
      {rows.length > 0 && <section className="panel"><PanelHeader eyebrow="Kohorten" title="Detailwerte" /><div className="table-scroll"><table className="data-table"><thead><tr><th>Tag</th><th>Markt</th><th>Sprache</th><th>Themengruppe</th><th>Impressions</th><th>Interaktionen</th><th>Relevanz</th></tr></thead><tbody>{rows.map((row, index) => <tr key={`${row.day}-${row.country_cluster}-${row.language}-${row.category_group}-${index}`}><td>{row.day}</td><td>{row.country_cluster}</td><td>{row.language.toUpperCase()}</td><td>{row.category_group}</td><td>{formatNumber(row.impressions)}</td><td>{formatNumber(row.interactions)}</td><td>{row.relevance === null ? '–' : row.relevance.toFixed(1)}</td></tr>)}</tbody></table></div></section>}
    </div>
  );
}

function MarketPolicies({ onSuccess, onError }: { onSuccess: (message: string) => void; onError: (error: unknown) => void }) {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState<MarketPolicyResult[]>([]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPolicies(await portalApi.marketPolicies());
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const country = String(data.get('country')).toUpperCase();
    const input: MarketPolicyInput = { status: String(data.get('status')) as MarketPolicyInput['status'], reasonCode: String(data.get('reasonCode')), policyVersion: String(data.get('policyVersion')), mediaRegion: String(data.get('mediaRegion')), liveAnalysisEnabled: data.get('liveAnalysisEnabled') === 'on' };
    setSaving(true);
    try {
      const result = await portalApi.updateMarketPolicy(country, input);
      setPolicies((items) => [...items.filter((item) => item.country !== result.country), result].sort((a, b) => a.country.localeCompare(b.country)));
      onSuccess(`Marktregel für ${result.country} wurde gespeichert.`);
    } catch (error) { onError(error); } finally { setSaving(false); }
  }
  return (
    <div className="page-stack page-stack--narrow">
      <PageIntro eyebrow="Platform Governance" title="Marktregeln" text="Verfügbarkeit, Medienregion und Live-Analyse werden serverseitig pro Land durchgesetzt." />
      <div className="admin-warning"><AlertCircle /><div><strong>Wirkt sofort auf neue Sessions</strong><span>Speichern ersetzt die Policy des eingegebenen Landes vollständig. Russland bleibt durch eine serverseitige Invariante gesperrt.</span></div></div>
      <section className="panel policy-editor">
        <PanelHeader eyebrow="Policy Editor" title="Land konfigurieren" />
        <form className="policy-form" onSubmit={submit}>
          <div className="field-grid field-grid--country"><Field label="Ländercode" htmlFor="policy-country"><input id="policy-country" name="country" pattern="[A-Za-z]{2}" maxLength={2} required placeholder="AT" /></Field><Field label="Status" htmlFor="policy-status"><label className="select-field select-field--full"><select id="policy-status" name="status" defaultValue="enabled"><option value="enabled">Aktiv</option><option value="waitlist">Warteliste</option><option value="blocked">Gesperrt</option></select><ChevronDown /></label></Field></div>
          <div className="field-grid"><Field label="Reason Code" htmlFor="policy-reason"><input id="policy-reason" name="reasonCode" minLength={2} maxLength={100} required placeholder="launch_market" /></Field><Field label="Policy-Version" htmlFor="policy-version"><input id="policy-version" name="policyVersion" minLength={3} maxLength={40} required placeholder="2026-09-08.2" /></Field></div>
          <Field label="Mediaregion" htmlFor="policy-region"><label className="select-field select-field--full"><select id="policy-region" name="mediaRegion" defaultValue="auto"><option value="auto">Automatisch</option><option value="eu">Europäische Union</option><option value="none">Keine Mediaregion</option></select><ChevronDown /></label></Field>
          <label className="switch-row"><span><strong>Live-Analyse erlauben</strong><small>Steuert die Sessionanalyse zusätzlich zur verpflichtenden Einwilligung.</small></span><input name="liveAnalysisEnabled" type="checkbox" defaultChecked /><i /></label>
          <button className="button button--primary" disabled={saving} type="submit">{saving ? <LoaderCircle className="spin" /> : <Check />} {saving ? 'Policy wird gespeichert' : 'Policy speichern'}</button>
        </form>
      </section>
      <section className="panel"><PanelHeader eyebrow="Aktueller Stand" title="Marktregeln" action={<button className="button button--secondary button--icon" onClick={() => void load()} disabled={loading} aria-label="Marktregeln neu laden"><RefreshCw className={loading ? 'spin' : ''} /></button>} />{loading ? <LoadingRows /> : policies.length ? <div className="policy-results">{policies.map((policy) => <div key={policy.country}><span className="country-badge">{policy.country}</span><div><strong>{policy.status}</strong><small>{policy.policyVersion} · {policy.reasonCode}</small></div><span>{policy.mediaRegion} · Analyse {policy.liveAnalysisEnabled ? 'an' : 'aus'}</span></div>)}</div> : <EmptyState icon={Globe2} title="Keine Marktregeln" text="Die API hat keine konfigurierten Märkte zurückgegeben." />}</section>
    </div>
  );
}

function Moderation({ role, onSuccess, onError }: { role: PortalRole; onSuccess: (message: string) => void; onError: (error: unknown) => void }) {
  const [reports, setReports] = useState<ModerationReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const load = useCallback(async () => { setLoading(true); try { setReports(await portalApi.moderationReports()); } catch (error) { onError(error); } finally { setLoading(false); } }, []);
  useEffect(() => { void load(); }, [load]);

  async function act(report: ModerationReport, action: 'claim' | 'resolve' | 'dismiss') {
    let resolutionCode = '';
    if (action !== 'claim') {
      const answer = window.prompt(
        action === 'resolve' ? 'Resolution Code (z. B. action_taken)' : 'Dismissal Code (z. B. no_violation)',
        action === 'resolve' ? 'action_taken' : 'no_violation',
      );
      if (answer === null) return;
      resolutionCode = answer.trim();
      if (resolutionCode.length < 2) {
        onError(new Error('Der Resolution Code muss mindestens zwei Zeichen enthalten.'));
        return;
      }
    }
    setBusyId(report.id);
    try {
      const updated = action === 'claim'
        ? await portalApi.claimModerationReport(report.id)
        : await portalApi.closeModerationReport(report.id, action, resolutionCode);
      setReports((items) => items.map((item) => (item.id === updated.id ? updated : item)));
      onSuccess(action === 'claim' ? 'Der Fall wurde dir zugewiesen.' : `Der Fall wurde als ${updated.status} abgeschlossen.`);
    } catch (error) {
      onError(error);
      await load();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page-stack">
      <PageIntro eyebrow="Trust & Safety" title="Moderationsmeldungen" text="Personenbezogene Gesprächsinhalte bleiben verborgen; sichtbar sind nur die im Trust-System freigegebenen Fallmetadaten." />
      <section className="panel">
        <div className="toolbar"><div className="moderation-summary"><span className="pulse-dot" /><strong>{reports.filter((report) => report.status === 'open').length}</strong> offene Meldungen</div><button className="button button--secondary" onClick={() => void load()} disabled={loading}><RefreshCw className={loading ? 'spin' : ''} /> Aktualisieren</button></div>
        {loading ? <LoadingRows /> : reports.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>Fall-ID</th><th>Grund</th><th>Status</th><th>Session-Referenz</th><th>Eingang</th><th><span className="sr-only">Aktionen</span></th></tr></thead><tbody>{reports.map((report) => {
          const canClose = (report.status === 'reviewing' && report.claimed_by_me)
            || (role === 'platform_admin' && (report.status === 'open' || report.status === 'reviewing'));
          return <tr key={report.id}><td><code>{report.id.slice(0, 8)}</code></td><td><div className="reason-cell"><MessageSquareWarning /><strong>{report.reason_code}</strong></div></td><td><span className={`report-state report-state--${report.status}`}>{report.status}</span>{report.status === 'reviewing' && <small className="claim-label">{report.claimed_by_me ? 'Dir zugewiesen' : 'In Bearbeitung'}</small>}</td><td><code>{report.room_hash.slice(0, 12)}…</code></td><td>{formatDateTime(report.created_at)}</td><td><div className="moderation-actions">{report.status === 'open' && <button className="button button--secondary button--small" disabled={busyId === report.id} onClick={() => void act(report, 'claim')}>Übernehmen</button>}{canClose && <><button className="button button--secondary button--small" disabled={busyId === report.id} onClick={() => void act(report, 'dismiss')}>Verwerfen</button><button className="button button--primary button--small" disabled={busyId === report.id} onClick={() => void act(report, 'resolve')}>{busyId === report.id ? <LoaderCircle className="spin" /> : <Check />} Lösen</button></>}</div></td></tr>;
        })}</tbody></table></div> : <EmptyState icon={ShieldCheck} title="Keine Meldungen in der Queue" text="Der Trust-Endpunkt hat aktuell keine Fälle zurückgegeben." />}
        <div className="api-boundary"><LockKeyhole /><span>Claims sind exklusiv und Statuswechsel werden mit pseudonymer Reviewer-Referenz im Trust-Audit protokolliert.</span></div>
      </section>
    </div>
  );
}

function PageIntro({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return <header className="page-intro"><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p className="muted">{text}</p></header>;
}

function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: ReactNode }) {
  return <div className="field"><label htmlFor={htmlFor}>{label}</label>{children}{hint && <small>{hint}</small>}</div>;
}

function InlineError({ title, detail }: { title: string; detail?: string }) {
  return <div className="inline-error" role="alert"><AlertCircle /><div><strong>{title}</strong>{detail && <span>{detail}</span>}</div></div>;
}

function EmptyState({ icon: Icon, title, text, action }: { icon: typeof Megaphone; title: string; text: string; action?: ReactNode }) {
  return <div className="empty-state"><span><Icon /></span><h4>{title}</h4><p>{text}</p>{action}</div>;
}

function LoadingRows() {
  return <div className="loading-rows" aria-label="Daten werden geladen">{[0, 1, 2].map((item) => <div key={item}><span /><span /><span /></div>)}</div>;
}

function RestrictedState() {
  return <section className="panel"><EmptyState icon={LockKeyhole} title="Berechtigung erforderlich" text="Deine Portalrolle ist für diesen Bereich nicht freigeschaltet." /></section>;
}

function Toast({ notice, onClose }: { notice: { kind: 'success' | 'error'; title: string; detail?: string } | null; onClose: () => void }) {
  useEffect(() => { if (!notice || notice.kind === 'error') return; const timer = window.setTimeout(onClose, 4800); return () => window.clearTimeout(timer); }, [notice, onClose]);
  if (!notice) return null;
  return <div className={`toast toast--${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.kind === 'success' ? <CheckCircle2 /> : <AlertCircle />}<div><strong>{notice.title}</strong>{notice.detail && <span>{notice.detail}</span>}</div><button className="icon-button" onClick={onClose} aria-label="Hinweis schließen"><X /></button></div>;
}
